import React, { useCallback } from 'react';
import { MentionPickerHandle } from '../MentionPicker/MentionPicker';
import { MessageContent } from 'flow/components/tickets/UseAttachmentHandler';

interface UseTicketInputKeyboardParams {
    showMentionPicker: boolean;
    setShowMentionPicker: (show: boolean) => void;
    mentionPickerRef: React.RefObject<MentionPickerHandle>;
    tabSelected: 'internal' | 'reply_external';
    messageContentInternal: MessageContent;
    messageContentReplyExternal: MessageContent;
    sendMessage: () => void | Promise<void>;
}

export const useTicketInputKeyboard = ({
    showMentionPicker,
    setShowMentionPicker,
    mentionPickerRef,
    tabSelected,
    messageContentInternal,
    messageContentReplyExternal,
    sendMessage,
}: UseTicketInputKeyboardParams) => {
    const handleKeyDown = useCallback(
        async (evt: React.KeyboardEvent<HTMLElement>) => {
            // Handle mention picker navigation
            if (showMentionPicker) {
                switch (evt.key) {
                    case 'ArrowUp':
                        evt.preventDefault();
                        mentionPickerRef?.current?.moveSelectionUp();
                        return;
                    case 'ArrowDown':
                        evt.preventDefault();
                        mentionPickerRef?.current?.moveSelectionDown();
                        return;
                    case 'Enter':
                        evt.preventDefault();
                        mentionPickerRef?.current?.confirmSelection();
                        setShowMentionPicker(false);
                        return;
                    case 'Escape':
                    case 'Tab':
                        setShowMentionPicker(false);
                        return;
                    default:
                        break;
                }
            }

            if (evt.key !== 'Enter') return;

            // Require Ctrl/Cmd+Enter to send in tickets
            if (!evt.ctrlKey && !evt.metaKey) return;

            evt.preventDefault();

            const messageContent =
                tabSelected === 'internal'
                    ? messageContentInternal
                    : messageContentReplyExternal;

            if (messageContent?.text?.trim() || messageContent?.attachment) {
                await sendMessage();
            }
        },
        [
            showMentionPicker,
            mentionPickerRef,
            setShowMentionPicker,
            tabSelected,
            messageContentInternal,
            messageContentReplyExternal,
            sendMessage,
        ],
    );

    return { handleKeyDown };
};
